'use client';
import React from 'react';
import Link from 'next/link';
import MagneticButton from './MagneticButton';
import { handleHashLinkClick, scrollToHash } from '@/components/experience/smoothScroll';

type Variant = 'primary' | 'ghost-dark' | 'ghost-light';

interface CTAButtonProps {
  href: string;
  children: React.ReactNode;
  variant?: Variant;
  arrow?: boolean;
  magnetic?: boolean;
  className?: string;
}

const variantClass: Record<Variant, string> = {
  primary: 'v2-btn-primary',
  'ghost-dark': 'v2-btn-ghost-dark',
  'ghost-light': 'v2-btn-ghost-light',
};

function Arrow() {
  return (
    <svg className="v2-btn-arrow h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 8h9.5M8.5 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * V2 call-to-action link. Handles same-page hashes with smooth scrolling, other routes via
 * next/link. Hover: slight scale, arrow nudge, background shift and a subtle magnetic pull.
 */
export function CTAButton({
  href,
  children,
  variant = 'primary',
  arrow = true,
  magnetic = true,
  className = '',
}: CTAButtonProps) {
  const classes = `v2-btn ${variantClass[variant]} ${className}`;
  const content = (
    <>
      <span>{children}</span>
      {arrow && <Arrow />}
    </>
  );

  const link = href.startsWith('#') ? (
    <a
      href={href}
      className={classes}
      onClick={(e) => {
        if (scrollToHash(href)) e.preventDefault();
      }}
    >
      {content}
    </a>
  ) : (
    <Link href={href} className={classes} onClick={(e) => handleHashLinkClick(e, href)}>
      {content}
    </Link>
  );

  return magnetic ? <MagneticButton>{link}</MagneticButton> : link;
}
